import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type { Hostel, PageResponse } from '@/api/types';
import { CheckCircle, XCircle, PauseCircle } from 'lucide-react';
import { hostelStatusLabel } from '@/lib/labels';

type StatusFilter = 'ALL' | 'PENDING_APPROVAL' | 'ACTIVE' | 'REJECTED' | 'SUSPENDED';

const STATUS_COLORS: Record<string, string> = {
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  ACTIVE:           'bg-green-100 text-green-800',
  REJECTED:         'bg-red-100 text-red-700',
  SUSPENDED:        'bg-gray-100 text-gray-700',
};

export function AdminHostelsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const { data, isLoading } = useQuery<PageResponse<Hostel>>({
    queryKey: ['admin-hostels'],
    queryFn: () => client.get('/api/admin/hostels?size=200').then(r => r as unknown as PageResponse<Hostel>),
  });

  const approve = useMutation({
    mutationFn: (id: string) => client.post(`/api/admin/hostels/${id}/approve`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-hostels'] }),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      client.post(`/api/admin/hostels/${id}/reject?reason=${encodeURIComponent(reason)}`),
    onSuccess: () => { setRejectId(null); setReason(''); void qc.invalidateQueries({ queryKey: ['admin-hostels'] }); },
  });

  const suspend = useMutation({
    mutationFn: (id: string) => client.post(`/api/admin/hostels/${id}/suspend`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-hostels'] }),
  });

  const all = data?.content ?? [];
  const counts: Record<StatusFilter, number> = {
    ALL:              all.length,
    PENDING_APPROVAL: all.filter(h => h.status === 'PENDING_APPROVAL').length,
    ACTIVE:           all.filter(h => h.status === 'ACTIVE').length,
    REJECTED:         all.filter(h => h.status === 'REJECTED').length,
    SUSPENDED:        all.filter(h => h.status === 'SUSPENDED').length,
  };
  const hostels = statusFilter === 'ALL' ? all : all.filter(h => h.status === statusFilter);

  const TABS: { key: StatusFilter; label: string }[] = [
    { key: 'ALL',              label: `${t('common.all')} (${counts.ALL})` },
    { key: 'PENDING_APPROVAL', label: `${t('rent.pending')} (${counts.PENDING_APPROVAL})` },
    { key: 'ACTIVE',           label: `${t('common.active')} (${counts.ACTIVE})` },
    { key: 'SUSPENDED',        label: `${t('admin.suspended', 'Suspended')} (${counts.SUSPENDED})` },
    { key: 'REJECTED',         label: `${t('admin.rejected', 'Rejected')} (${counts.REJECTED})` },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('nav.hostels')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('admin.hostelsSubtitle', 'Manage and approve hostels across the platform')}</p>
        </div>
        {counts.PENDING_APPROVAL > 0 && (
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium">
            {t('admin.pendingApprovalCount', '{{count}} pending approval', { count: counts.PENDING_APPROVAL })}
          </span>
        )}
      </div>

      {/* Status tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit flex-wrap">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
              statusFilter === tab.key ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-gray-400">{t('common.loading')}</p>
      ) : hostels.length === 0 ? (
        <div className="bg-white rounded-xl p-10 text-center text-gray-400 border border-dashed border-gray-200">
          {t('admin.noHostelsInCategory', 'No hostels in this category')}
        </div>
      ) : (
        <div className="grid gap-4">
          {hostels.map(h => (
            <div key={h.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{h.name}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[h.status] ?? 'bg-gray-100 text-gray-700'}`}>
                      {hostelStatusLabel(t, h.status)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{h.address}, {h.city}, {h.state} – {h.pincode}</p>
                  {h.phone && <p className="text-sm text-gray-500 mt-0.5">📞 {h.phone}</p>}
                  {h.ownerName && (
                    <p className="text-sm text-gray-500 mt-0.5">
                      {t('hostel.owner', 'Owner')}: {h.ownerName}
                      {h.ownerPhone && ` (${h.ownerPhone})`}
                    </p>
                  )}
                  <p className="text-sm text-gray-500 mt-0.5">
                    {t('hostel.supervisors', 'Supervisors')}:{' '}
                    {h.supervisors && h.supervisors.length > 0
                      ? h.supervisors.map(s => s.fullName).join(', ')
                      : t('hostel.noSupervisorAssigned', 'None assigned')}
                  </p>
                  {h.rejectionReason && (
                    <p className="text-xs text-red-500 mt-1">{t('admin.rejectionReasonLabel', 'Rejection reason:')} {h.rejectionReason}</p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                  {h.status === 'PENDING_APPROVAL' && (
                    <>
                      <button
                        onClick={() => approve.mutate(h.id)}
                        disabled={approve.isPending}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-40"
                      >
                        <CheckCircle size={15} /> {t('admin.approve', 'Approve')}
                      </button>
                      <button
                        onClick={() => { setRejectId(h.id); setReason(''); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
                      >
                        <XCircle size={15} /> {t('admin.reject', 'Reject')}
                      </button>
                    </>
                  )}
                  {h.status === 'ACTIVE' && (
                    <button
                      onClick={() => suspend.mutate(h.id)}
                      disabled={suspend.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-600 disabled:opacity-40"
                    >
                      <PauseCircle size={15} /> {t('admin.suspend', 'Suspend')}
                    </button>
                  )}
                  {(h.status === 'REJECTED' || h.status === 'SUSPENDED') && (
                    <button
                      onClick={() => approve.mutate(h.id)}
                      disabled={approve.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-40"
                    >
                      <CheckCircle size={15} /> {t('admin.reactivate', 'Re-activate')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject modal */}
      {rejectId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-chrome-page rounded-xl p-6 w-96 shadow-xl">
            <h3 className="font-semibold mb-4">{t('admin.reasonForRejection', 'Reason for Rejection')}</h3>
            <textarea
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder={t('admin.enterRejectionReason', 'Enter rejection reason…')}
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4 resize-none"
            />
            <div className="flex gap-2">
              <button
                onClick={() => reject.mutate({ id: rejectId, reason })}
                disabled={!reason.trim() || reject.isPending}
                className="flex-1 bg-red-600 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-40"
              >
                {reject.isPending ? t('admin.rejecting', 'Rejecting…') : t('admin.reject', 'Reject')}
              </button>
              <button onClick={() => setRejectId(null)} className="flex-1 border rounded-lg py-2 text-sm">{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
